import React, { useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Menu } from "@mui/material";
import { Button, makeStyles } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StyledMenuItem } from "../../../pages/LandingPage/Header/StyledComponent";
import Colors from "../../../common/materials/colors";
import { oAuth } from "../../../routes/basic/BasicAppRouter";
import { MobileSignIn } from "../../../pages/zipang/Header/ZipangHeader";
import { DialogProvider } from "../../../common/elements/Dialog/Dialog";
import auctions, {
  getPastProducts,
} from "../../../services/ProductsApiService";
import Alert from "../../../common/elements/Alert";

const StyledRelativeComponent = styled.div`
  position: relative;
  display: flex;
  gap: 10px;
`;

const StyledButton = styled(Button)`
  color: #000f7e;
  text-transform: capitalize;
  font-weight: 600;
  &:focus {
    outline: none;
  }
`;

const StyledMenuContainer = styled.div``;
const faChevronDownProps = faChevronDown as IconProp;

const useStyles = makeStyles({
  paper: {
    borderTop: `4px solid ${Colors.darkBlue}`,
  },
});

interface Iprops {
  menuData: any;
}

const HeaderMenu = (props: Iprops) => {
  const [showSignIn, setShowSignIn] = useState(false);
  const handleSigninClose = () => {
    setShowSignIn(false);
    localStorage.removeItem("postLoginRedirect");
  };

  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuSpace = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const headerMenuItems = useRef(null);
  const classes = useStyles();
  const [openToast, setOpenToast] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState("");
  const handleClick = (event: any) => {
    headerMenuItems.current = event.currentTarget.id;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  let languageCode =
    localStorage.getItem("language") === "EN"
      ? "en"
      : localStorage.getItem("language") === "zh_CN"
      ? "zh_CN"
      : "jp";

  const handleRedirect = (url: string) => {
    if (oAuth() === false) {
      setShowSignIn(true);

      const localizedUrl = `/${languageCode}${url}`;
      localStorage.setItem("postLoginRedirect", localizedUrl);
    } else {
      history.push(url);
      handleClose();
    }
  };

  const headerButtonHandler = async (routeName: string) => {
    if (
      oAuth() === false &&
      routeName !== "/home" &&
      routeName !== "/preview"
    ) {
      setShowSignIn(true);

      const localizedUrl = `/${languageCode}${routeName}`;
      localStorage.setItem("postLoginRedirect", localizedUrl);
    } else if (routeName === "/preview") {
      try {
        //  1. Check ongoing auctions
        const auctionlist = await auctions();

        if (auctionlist?.results?.length > 0) {
          const {
            auctionType,
            id: auctionId,
            auctionUserId,
            name: auctionName,
            startDate: auctionDate,
          } = auctionlist.results[auctionlist.results.length - 1];

          if (auctionType && auctionType !== "Live") {
            history.push({
              pathname: `/preview/tendor/products/${auctionId}/${auctionUserId}/${auctionName}/${auctionDate}`,
            });
          } else {
            history.push({
              pathname: `/preview/products/${auctionId}/${encodeURIComponent(
                auctionName
              )}/${encodeURIComponent(auctionDate)}`,
            });
          }
          return; // stop execution
        }

        // 2. If no ongoing auction → check past auctions
        const pastAuctionProducts = await getPastProducts();

        if (pastAuctionProducts?.results?.length > 0) {
          history.push({
            pathname: "/publicpreview/pastproducts",
            search: "?pastProducts=true",
          });
          return;
        }

        //  3. If neither ongoing nor past auctions
        setShowAlertMessage("No Ongoing  Auction found");
        setOpenToast(true);
        setTimeout(() => {
          setOpenToast(false);
        }, 3000);
        setTimeout(() => {
          history.push("/home");
        }, 3000);
      } catch (err) {
        console.error("Error fetching auctions:", err);
        setShowAlertMessage("Something went wrong. Please try again later.");
        setOpenToast(true);
      }
    } else {
      history.push(routeName);
    }
  };

  //  Dynamic route matcher
  const isActive = (path: string): boolean => {
    const current = location.pathname;

    if (path === "/preview") {
      return (
        current.startsWith("/preview") || current.startsWith("/publicpreview")
      );
    }

    if (path === "/team-page") {
      return current.startsWith("/team-page");
    }

    return current === path;
  };

  return (
    <DialogProvider>
      {openToast === true && (
        <Alert showAlertMessage={showAlertMessage} alertType={"error"} />
      )}
      <StyledRelativeComponent>
        {props.menuData.map((item: any, index: number) => (
          <React.Fragment key={index}>
            {item.subNav === undefined ? (
              <StyledButton
                onClick={() => headerButtonHandler(item.path)}
                className={isActive(item.path) ? "is-active" : ""}
              >
                <span>{t(item.title)}</span>
              </StyledButton>
            ) : (
              <StyledButton
                aria-controls="simple-menu"
                aria-haspopup="true"
                onClick={handleClick}
                id={item.path}
                className={isActive(item.path) ? "is-active" : ""}
              >
                <span>{t(item.title)}</span>
                &nbsp;
                <FontAwesomeIcon
                  icon={faChevronDownProps}
                  style={{ color: Colors.bidBtnBackground }}
                />
              </StyledButton>
            )}
          </React.Fragment>
        ))}

        <StyledMenuContainer ref={menuSpace} />

        {props.menuData
          .filter((item: any) => item.path === headerMenuItems.current)
          .map((subItems: any, index: number) => (
            <div key={index}>
              {subItems.subNav && (
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  classes={classes}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  sx={{ mt: "35px", fontSize: "0.8em" }}
                >
                  {subItems.subNav.map((subItem: any, subIndex: number) => (
                    <StyledMenuItem
                      key={subIndex}
                      onClick={() => handleRedirect(subItem.path)}
                      className={isActive(subItem.path) ? "is-active" : ""}
                    >
                      {t(subItem.title)}
                    </StyledMenuItem>
                  ))}
                </Menu>
              )}
            </div>
          ))}

        <MobileSignIn show={showSignIn} onClose={handleSigninClose} />
      </StyledRelativeComponent>
    </DialogProvider>
  );
};

export default React.memo(HeaderMenu);
